import { 
  collection, 
  doc, 
  getDocs, 
  addDoc,
  query, 
  where, 
  orderBy,
  limit,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';

// Discount Usage Interface
export interface DiscountUsage {
  id: string;
  discountCodeId: string;
  discountCode: string;        // The actual code used
  orderId: string;
  userId: string;
  userEmail: string;
  discountAmount: number;      // Amount saved
  orderTotal: number;          // Total order value
  usedAt: Date;
  ipAddress?: string;          // For fraud detection
  userAgent?: string;          // For fraud detection
}

/**
 * Log discount code usage
 */
export const logDiscountUsage = async (usageData: Omit<DiscountUsage, 'id' | 'usedAt'>): Promise<string> => {
  try {
    const usageRef = collection(db, 'discount_usage');
    const docRef = await addDoc(usageRef, {
      ...usageData,
      usedAt: new Date()
    });
    
    console.log('Discount usage logged:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error logging discount usage:', error);
    throw new Error('Failed to log discount usage');
  }
};

/**
 * Get discount usage analytics for a specific discount code
 */
export const getDiscountAnalytics = async (discountCodeId: string) => {
  try {
    const usageQuery = query(
      collection(db, 'discount_usage'),
      where('discountCodeId', '==', discountCodeId),
      orderBy('usedAt', 'desc')
    );
    
    const snapshot = await getDocs(usageQuery);
    const usageData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      usedAt: doc.data().usedAt?.toDate() || new Date()
    })) as DiscountUsage[];
    
    const totalUses = usageData.length;
    const totalSavings = usageData.reduce((sum, usage) => sum + usage.discountAmount, 0);
    const totalRevenue = usageData.reduce((sum, usage) => sum + usage.orderTotal, 0);
    
    return {
      totalUses,
      totalSavings,
      totalRevenue,
      averageOrderValue: totalUses > 0 ? totalRevenue / totalUses : 0,
      recentUsage: usageData.slice(0, 10),
      usageData
    };
  } catch (error) {
    console.error('Error fetching discount analytics:', error);
    throw new Error('Failed to fetch discount analytics');
  }
};

/**
 * Get user-specific usage count for a discount code
 */
export const getUserUsageCount = async (discountCodeId: string, userId: string): Promise<number> => {
  try {
    const userUsageQuery = query(
      collection(db, 'discount_usage'),
      where('discountCodeId', '==', discountCodeId),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(userUsageQuery);
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching user usage count:', error);
    return 0; // Return 0 on error to be safe
  }
};

/**
 * Get all discount usage records with pagination
 */
export const getDiscountUsageHistory = async (limitCount: number = 50) => {
  try {
    const usageQuery = query(
      collection(db, 'discount_usage'),
      orderBy('usedAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(usageQuery);
    const usageData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      usedAt: doc.data().usedAt?.toDate() || new Date()
    })) as DiscountUsage[];
    
    return usageData;
  } catch (error) {
    console.error('Error fetching discount usage history:', error);
    throw new Error('Failed to fetch discount usage history');
  }
};

/**
 * Get usage statistics for all discount codes
 */
export const getAllDiscountStatistics = async () => {
  try {
    const usageQuery = query(
      collection(db, 'discount_usage'),
      orderBy('usedAt', 'desc')
    );
    
    const snapshot = await getDocs(usageQuery);
    const usageData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      usedAt: doc.data().usedAt?.toDate() || new Date()
    })) as DiscountUsage[];
    
    // Group by discount code
    const statsByCode = usageData.reduce((acc, usage) => {
      const code = usage.discountCode;
      if (!acc[code]) {
        acc[code] = {
          discountCode: code,
          discountCodeId: usage.discountCodeId,
          totalUses: 0,
          totalSavings: 0,
          totalRevenue: 0,
          lastUsed: usage.usedAt
        };
      }
      
      acc[code].totalUses++;
      acc[code].totalSavings += usage.discountAmount;
      acc[code].totalRevenue += usage.orderTotal;
      
      if (usage.usedAt > acc[code].lastUsed) {
        acc[code].lastUsed = usage.usedAt;
      }
      
      return acc;
    }, {} as Record<string, any>);
    
    return Object.values(statsByCode);
  } catch (error) {
    console.error('Error fetching discount statistics:', error);
    throw new Error('Failed to fetch discount statistics');
  }
}; 